// Pure conflict-detectie tussen een inkoop-order (per leverancier) en de
// leveranciersvoorwaarden. Geen UI/IO; los testbaar (zelfde patroon als reorder.ts).

export interface SupplierTerms {
  min_order_value?: number | null;
  transport_cost_below_min?: number | null;
  order_lead_time_days?: number | null;
}

export interface ConflictItem {
  name: string;
  /** Totaal te bestellen stuks (na colli-afronding). */
  total_ordered: number;
  min_order_qty?: number | null;
  /** ISO-datum (YYYY-MM-DD) vanaf wanneer leverbaar, of null. */
  available_from?: string | null;
}

export interface SupplierConflictInput {
  supplierName: string;
  terms: SupplierTerms;
  /** Inkoopwaarde excl. btw voor deze leverancier. */
  purchaseTotalExclBtw: number;
  items: ConflictItem[];
  /** Gewenste leverdatum (ISO YYYY-MM-DD), of null. */
  handlingDate?: string | null;
  /** Referentiedatum (default: nu). */
  today?: Date;
}

export type ConflictType = 'min_order_value' | 'lead_time' | 'availability' | 'product_moq';

export interface SupplierConflict {
  supplier: string;
  type: ConflictType;
  severity: 'warn';
  message: string;
}

function euro(n: number): string {
  return `EUR ${n.toFixed(2)}`;
}

/** Parse een ISO-datum (YYYY-MM-DD) naar een Date op middernacht UTC, of null. */
function parseDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatNl(d: Date): string {
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getUTCFullYear()}`;
}

function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export function detectSupplierConflicts(input: SupplierConflictInput): SupplierConflict[] {
  const { supplierName, terms, purchaseTotalExclBtw, items, handlingDate } = input;
  const today = input.today ?? new Date();
  const conflicts: SupplierConflict[] = [];

  // 1. Minimum orderbedrag
  if (terms.min_order_value != null && purchaseTotalExclBtw < terms.min_order_value) {
    const transport =
      terms.transport_cost_below_min != null
        ? `, anders ${euro(terms.transport_cost_below_min)} transportkosten`
        : '';
    conflicts.push({
      supplier: supplierName,
      type: 'min_order_value',
      severity: 'warn',
      message: `Onder minimum order: ${euro(purchaseTotalExclBtw)} < ${euro(terms.min_order_value)}${transport}.`,
    });
  }

  // 2. Levertijd vs gewenste leverdatum
  const handling = parseDate(handlingDate);
  if (terms.order_lead_time_days != null && handling) {
    const days = daysBetween(today, handling);
    if (days < terms.order_lead_time_days) {
      conflicts.push({
        supplier: supplierName,
        type: 'lead_time',
        severity: 'warn',
        message: `Levertijd is ${terms.order_lead_time_days} dagen, maar de leverdatum is over ${days} dagen (${formatNl(handling)}).`,
      });
    }
  }

  // 3. Product-beschikbaarheid (leverbaar vanaf > leverdatum)
  if (handling) {
    for (const it of items) {
      const avail = parseDate(it.available_from);
      if (avail && avail.getTime() > handling.getTime()) {
        conflicts.push({
          supplier: supplierName,
          type: 'availability',
          severity: 'warn',
          message: `${it.name} is pas leverbaar vanaf ${formatNl(avail)}, na de gewenste leverdatum (${formatNl(handling)}).`,
        });
      }
    }
  }

  // 4. Minimum bestelhoeveelheid per product
  for (const it of items) {
    if (it.min_order_qty != null && it.total_ordered > 0 && it.total_ordered < it.min_order_qty) {
      conflicts.push({
        supplier: supplierName,
        type: 'product_moq',
        severity: 'warn',
        message: `${it.name}: ${it.total_ordered} besteld, onder minimum van ${it.min_order_qty}.`,
      });
    }
  }

  return conflicts;
}
